import React, { useState, useEffect } from "react";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} €`;

const B2BPaymentModal = ({ order, remainingBalance, onClose, onApplyPayment }) => {
  const [selectedMethod, setSelectedMethod] = useState("ESPECES");
  const [currentInput, setCurrentInput] = useState(String(remainingBalance));
  
  // For Ticket Resto
  const [trValue, setTrValue] = useState("");
  const [trQty, setTrQty] = useState(1);
  
  // For N Fois
  const [nFois, setNFois] = useState([
    { date: "", method: "CHEQUE", ref: "", amount: "" },
    { date: "", method: "CHEQUE", ref: "", amount: "" },
    { date: "", method: "CHEQUE", ref: "", amount: "" }
  ]);
  
  // For Differe
  const [diffInvoice, setDiffInvoice] = useState("");
  const [diffRef, setDiffRef] = useState("");

  const methods = [
    { id: "ESPECES", label: "Espèces", icon: "bi-cash-coin" },
    { id: "CHEQUE", label: "Chèque", icon: "bi-card-heading" },
    { id: "CB", label: "Carte Bancaire", icon: "bi-credit-card" },
    { id: "VIREMENT", label: "Virement", icon: "bi-bank" },
    { id: "BON_ACHAT", label: "Bon d'achat", icon: "bi-gift" },
    { id: "TICKET_RESTO", label: "Ticket Resto", icon: "bi-cup-hot" },
    { id: "AVOIR", label: "Avoir", icon: "bi-arrow-return-left" },
    { id: "N_FOIS", label: "En N fois", icon: "bi-calendar3" },
    { id: "DIFFERE", label: "Différé", icon: "bi-clock-history" }
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handlePadClick = (val) => {
    if (val === "C") setCurrentInput("");
    else if (val === "DEL") setCurrentInput(prev => prev.slice(0, -1));
    else setCurrentInput(prev => prev + val);
  };

  const handleApply = () => {
    if (selectedMethod === "DIFFERE") {
      onApplyPayment({
        type: "DIFFERE",
        invoice: diffInvoice,
        ref: diffRef,
        amount: remainingBalance
      });
      return;
    }

    if (selectedMethod === "N_FOIS") {
      const validPayments = nFois.filter(p => p.amount && parseFloat(p.amount) > 0);
      const totalN = validPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
      if (Math.abs(totalN - remainingBalance) > 0.01) {
        alert(`Le total des échéances (${totalN} €) doit être égal au reste à payer (${remainingBalance} €).`);
        return;
      }
      onApplyPayment({
        type: "N_FOIS",
        installments: validPayments
      });
      return;
    }

    if (selectedMethod === "TICKET_RESTO") {
      const val = parseFloat(trValue) || 0;
      const q = parseInt(trQty) || 0;
      const tot = val * q;
      if (tot <= 0) return;
      onApplyPayment({
        type: "TICKET_RESTO",
        amount: tot,
        ticketValue: val,
        quantity: q
      });
      return;
    }

    const amt = parseFloat(currentInput);
    if (isNaN(amt) || amt <= 0) return;
    
    onApplyPayment({
      type: "STANDARD",
      method: selectedMethod,
      amount: amt
    });
  };

  const renderPad = () => (
    <div className="d-grid gap-2" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
      {["7","8","9","C","4","5","6","DEL","1","2","3","","0","00",".",""].map((btn, i) => (
        btn ? (
          <button 
            key={i} 
            className={`btn btn-light border fw-bold ${btn === 'C' || btn === 'DEL' ? 'text-danger' : 'text-dark'}`}
            style={{ height: "50px", fontSize: "1.2rem" }}
            onClick={() => handlePadClick(btn)}
          >
            {btn === 'DEL' ? <i className="bi bi-backspace"></i> : btn}
          </button>
        ) : <div key={i}></div>
      ))}
    </div>
  );

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1055 }}>
      <div className="modal-dialog modal-fullscreen p-4">
        <div className="modal-content border-0 bg-dark text-white shadow-lg d-flex flex-row" style={{ borderRadius: "12px", overflow: "hidden" }}>
          
          {/* LEFT PANEL: Payment methods & Input */}
          <div className="flex-grow-1 d-flex flex-column" style={{ backgroundColor: "#374151" }}>
            
            {/* Top Bar: Methods */}
            <div className="d-flex overflow-auto border-bottom border-secondary" style={{ backgroundColor: "#1f2937" }}>
              {methods.map(m => (
                <button
                  key={m.id}
                  className={`btn rounded-0 p-3 d-flex flex-column align-items-center justify-content-center flex-grow-1 ${selectedMethod === m.id ? 'bg-secondary text-white border-bottom border-warning border-3' : 'text-secondary'}`}
                  style={{ minWidth: "100px", border: "none" }}
                  onClick={() => {
                    setSelectedMethod(m.id);
                    if (m.id !== "N_FOIS" && m.id !== "DIFFERE" && m.id !== "TICKET_RESTO") {
                      setCurrentInput(String(remainingBalance));
                    }
                  }}
                >
                  <i className={`bi ${m.icon} mb-1 fs-4`}></i>
                  <span style={{ fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase" }}>{m.label}</span>
                </button>
              ))}
            </div>

            {/* Input Area */}
            <div className="flex-grow-1 p-5 d-flex flex-column justify-content-center">
              
              <h3 className="text-uppercase mb-4 fw-light text-warning border-bottom border-secondary pb-2">{methods.find(m => m.id === selectedMethod)?.label}</h3>

              {selectedMethod === "N_FOIS" ? (
                <div className="bg-dark p-4 rounded shadow-sm">
                  <div className="row fw-bold text-muted mb-2 small">
                    <div className="col-3">DATE</div>
                    <div className="col-3">MÉTHODE</div>
                    <div className="col-3">RÉFÉRENCE</div>
                    <div className="col-3">MONTANT</div>
                  </div>
                  {nFois.map((nf, idx) => (
                    <div className="row g-2 mb-3" key={idx}>
                      <div className="col-3">
                        <input type="date" className="form-control form-control-lg bg-secondary text-white border-secondary" value={nf.date} onChange={e => { const n = [...nFois]; n[idx].date = e.target.value; setNFois(n); }} />
                      </div>
                      <div className="col-3">
                        <select className="form-select form-select-lg bg-secondary text-white border-secondary" value={nf.method} onChange={e => { const n = [...nFois]; n[idx].method = e.target.value; setNFois(n); }}>
                          <option value="CHEQUE">Chèque</option>
                          <option value="VIREMENT">Virement</option>
                          <option value="CB">Carte Bancaire</option>
                        </select>
                      </div>
                      <div className="col-3">
                        <input type="text" className="form-control form-control-lg bg-secondary text-white border-secondary" placeholder="N°..." value={nf.ref} onChange={e => { const n = [...nFois]; n[idx].ref = e.target.value; setNFois(n); }} />
                      </div>
                      <div className="col-3">
                        <div className="input-group input-group-lg">
                          <input type="number" className="form-control bg-secondary text-white border-secondary text-end" value={nf.amount} onChange={e => { const n = [...nFois]; n[idx].amount = e.target.value; setNFois(n); }} />
                          <span className="input-group-text bg-dark border-secondary text-white">€</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="fs-5">Total planifié : {formatPrice(nFois.reduce((s, p) => s + (parseFloat(p.amount)||0), 0))}</span>
                    <button className="btn btn-info px-5 py-3 fw-bold fs-5" onClick={handleApply}>APPLIQUER</button>
                  </div>
                </div>
              ) : selectedMethod === "DIFFERE" ? (
                <div className="bg-dark p-4 rounded shadow-sm w-75">
                  <div className="mb-4">
                    <label className="text-muted small fw-bold mb-1">NUMÉRO DE FACTURE</label>
                    <input type="text" className="form-control form-control-lg bg-secondary text-white border-secondary" value={diffInvoice} onChange={e => setDiffInvoice(e.target.value)} />
                  </div>
                  <div className="mb-4">
                    <label className="text-muted small fw-bold mb-1">RÉFÉRENCE CLIENT</label>
                    <input type="text" className="form-control form-control-lg bg-secondary text-white border-secondary" value={diffRef} onChange={e => setDiffRef(e.target.value)} />
                  </div>
                  <button className="btn btn-info w-100 py-3 fw-bold fs-5" onClick={handleApply}>VALIDER LE DIFFÉRÉ</button>
                </div>
              ) : selectedMethod === "TICKET_RESTO" ? (
                <div className="row w-100">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="text-warning small fw-bold mb-1 text-uppercase">Valeur</label>
                      <div className="input-group input-group-lg shadow-sm">
                        <input type="number" className="form-control text-end fw-bold" style={{ fontSize: "1.5rem" }} value={trValue} onChange={e => setTrValue(e.target.value)} />
                        <span className="input-group-text bg-light text-dark fw-bold">€</span>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="text-warning small fw-bold mb-1 text-uppercase">Quantité</label>
                      <div className="d-flex">
                        <button className="btn btn-secondary fs-4 px-4" onClick={() => setTrQty(Math.max(1, trQty - 1))}>−</button>
                        <input type="number" className="form-control text-center fw-bold fs-4 bg-light text-dark mx-2" value={trQty} readOnly />
                        <button className="btn btn-secondary fs-4 px-4" onClick={() => setTrQty(trQty + 1)}>+</button>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between text-info fs-5 fw-bold mb-2">
                      <span>Total</span>
                      <span>{formatPrice((parseFloat(trValue)||0) * trQty)}</span>
                    </div>
                    <button className="btn btn-info w-100 py-3 fw-bold fs-5 mt-4" onClick={handleApply}>APPLIQUER</button>
                  </div>
                </div>
              ) : (
                /* STANDARD METHODS (Espèces, CB, Virement, Cheque, Avoir, Bon achat) */
                <div className="row w-100">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="text-warning small fw-bold mb-1 text-uppercase">Montant</label>
                      <div className="input-group input-group-lg shadow-sm">
                        <input type="text" className="form-control text-end fw-bold text-dark" style={{ fontSize: "1.5rem" }} value={currentInput} readOnly />
                        <span className="input-group-text bg-light text-dark fw-bold">€</span>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between text-info fs-5 fw-bold mb-4">
                      <span>Rendu ({methods.find(m => m.id === selectedMethod)?.label})</span>
                      <span>{formatPrice(Math.max(0, parseFloat(currentInput||0) - remainingBalance))}</span>
                    </div>
                    <button className="btn btn-info w-100 py-3 fw-bold fs-5" onClick={handleApply}>APPLIQUER</button>
                  </div>
                  <div className="col-md-5 offset-md-1">
                    {renderPad()}
                  </div>
                </div>
              )}
              
            </div>
          </div>

          {/* RIGHT PANEL: Summary */}
          <div className="d-flex flex-column" style={{ width: "350px", backgroundColor: "#9ca3af" }}>
            <div className="p-4 flex-grow-1 text-dark">
              <div className="d-flex justify-content-between align-items-end border-bottom border-secondary pb-2 mb-3">
                <span className="fw-bold" style={{ fontSize: "1rem" }}>{order.items?.length || 0} ARTICLES</span>
                <span className="fs-5">{formatPrice(order.amount)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="fw-bold text-uppercase" style={{ fontSize: "1.2rem" }}>Restant Dû</span>
                <span className="fw-bold text-white fs-2">{formatPrice(remainingBalance)}</span>
              </div>
            </div>
            
            <div className="p-3 bg-secondary d-flex justify-content-between text-white" style={{ fontSize: "0.8rem" }}>
              <span>Produits {order.items?.length || 0}</span>
              <span>Total {formatPrice(order.amount)}</span>
            </div>
            <button className="btn btn-warning rounded-0 fw-bold py-3 fs-5" onClick={onClose} style={{ color: "#92400e" }}>
              RETOUR
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default B2BPaymentModal;
